import { useEffect, useMemo, useRef, useState } from 'react';
import { NATIVE_OR_SIM } from '../lib/proximity/native/simPhone';
import { hasUserGroupSelectionArm, FIND_SESSION_GUARD } from '../config/findSessionGuard';
import { ensureFindRemoteLogPageContext, findRemoteLog } from '../lib/findRemoteLog';
import {
  logSessionRunKeyRecompute,
  parseSessionRunKey,
} from '../lib/proximity/sessionRunKeyInstrumentation';

const IS_NATIVE = NATIVE_OR_SIM;

/**
 * The group slot of a session key when there is no group: you are going to an event that is on, so your
 * position is published for the day you have, and the group you may join later (Sam, 2026-10-04: track anyone
 * with a live event, group or not). No peers are watched - there is nobody to watch yet.
 */
export const SOLO_SESSION_GROUP = 'solo';

function buildRawSessionRunKey(enabled, userId, groupId, solo, eventId, variant) {
  if (!enabled || !userId || !IS_NATIVE) {
    return null;
  }
  // A variant (the beacon mode, when not the default) is part of the key, so changing it restarts the session.
  const suffix = variant ? `#${variant}` : '';
  if (groupId) return `${userId}:${groupId}${suffix}`;
  return solo ? `${userId}:${SOLO_SESSION_GROUP}@${eventId ?? ''}${suffix}` : null;
}

/**
 * Debounce sessionRunKey churn. Only an armed group-picker commit bypasses debounce.
 */
export function useManagedSessionRunKey({
  enabled,
  userId,
  groupId,
  solo = false,
  eventId = null,
  variant = null,
}) {
  const rawSessionRunKey = useMemo(
    () => buildRawSessionRunKey(enabled, userId, groupId, solo, eventId, variant),
    [enabled, userId, groupId, solo, eventId, variant],
  );

  const [managedSessionRunKey, setManagedSessionRunKey] = useState(rawSessionRunKey);
  const managedRef = useRef(rawSessionRunKey);
  const debounceTimerRef = useRef(null);
  const pendingKeyRef = useRef(rawSessionRunKey);
  const instrumentPrevRef = useRef({
    enabled,
    userId,
    groupId,
    isNative: IS_NATIVE,
    rawKey: rawSessionRunKey,
  });

  useEffect(() => {
    void ensureFindRemoteLogPageContext({ userId, groupId });

    instrumentPrevRef.current = logSessionRunKeyRecompute(
      rawSessionRunKey,
      { enabled, userId, groupId, isNative: IS_NATIVE },
      instrumentPrevRef.current,
    );

    pendingKeyRef.current = rawSessionRunKey;

    const applyKey = (key, reason) => {
      if (managedRef.current === key) {
        return;
      }
      managedRef.current = key;
      setManagedSessionRunKey(key);
      findRemoteLog(
        'debug',
        'proximity.session_run_key.applied',
        {
          key,
          reason,
          pendingKey: pendingKeyRef.current,
        },
        { skipThrottle: true },
      );
    };

    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const previousKey = managedRef.current;
    const nextGroupId = parseSessionRunKey(rawSessionRunKey).groupId;
    const userPickerIntent = nextGroupId != null && hasUserGroupSelectionArm(nextGroupId);

    if (userPickerIntent) {
      applyKey(rawSessionRunKey, 'user-picker');
      return undefined;
    }

    if (rawSessionRunKey === previousKey) {
      return undefined;
    }

    const debounceMs = FIND_SESSION_GUARD.debounceMs;
    findRemoteLog(
      'debug',
      'proximity.session_run_key.debounced',
      {
        from: previousKey,
        to: rawSessionRunKey,
        debounceMs,
        fromGroupId: parseSessionRunKey(previousKey).groupId,
        toGroupId: nextGroupId,
      },
      { skipThrottle: true },
    );

    debounceTimerRef.current = window.setTimeout(() => {
      debounceTimerRef.current = null;
      if (pendingKeyRef.current === rawSessionRunKey) {
        applyKey(rawSessionRunKey, 'debounce-settled');
      }
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current != null) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [rawSessionRunKey, enabled, userId]);

  return { rawSessionRunKey, managedSessionRunKey, managedSessionRunKeyRef: managedRef };
}

export function waitForSessionMinLifetime(
  session,
  targetKey,
  managedRef,
  { userIntent = false } = {},
) {
  if (userIntent) {
    return Promise.resolve(false);
  }
  if (!session?.startedAt || session.disposed) {
    return Promise.resolve(false);
  }
  if (session.key === targetKey) {
    return Promise.resolve(true);
  }

  const elapsed = Date.now() - session.startedAt;
  const remaining = FIND_SESSION_GUARD.minLifetimeMs - elapsed;
  if (remaining <= 0) {
    return Promise.resolve(false);
  }

  findRemoteLog(
    'debug',
    'proximity.session.restart_deferred',
    {
      remainingMs: remaining,
      fromKey: session.key,
      toKey: targetKey,
    },
    { skipThrottle: true },
  );

  return new Promise((resolve) => {
    window.setTimeout(() => {
      resolve(managedRef.current !== targetKey);
    }, remaining);
  });
}
